import useRidesStore from '../../../stores/rides'
import { Avatar, Chip, IconButton, LinearProgress, Stack, TextField, Tooltip, Typography } from '@mui/material'
import PhoneIcon from '@mui/icons-material/Phone'
import PersonIcon from '@mui/icons-material/Person'
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar'
import LinkIcon from '@mui/icons-material/Link'
import moment from 'moment-timezone'
import LabelChip from '../../../components/ui/LabelChip'
import LoadingButton from '@mui/lab/LoadingButton'
import useCancelRideDialog from '../../../hooks/useCancelRideDialog'
import useRideByIdQuery from '../../../hooks/useRideByIdQuery'
import useAppStore from '../../../stores/app'
import { enqueueSnackbar } from 'notistack'
import { ridesTypesWavIds } from '../ridesConfig'
import AccessibleIcon from '@mui/icons-material/Accessible'
import { formatPhoneNumber } from '../../../utils/utils-functions'
import useUserIsReadOnly from '../../../hooks/useUserIsReadOnly'

const RideInfo = () => {
  const selectedRideId = useRidesStore(state => state.selectedRideId)
  const equipment_codes = useAppStore(state => state.equipment_codes)
  const isRideOnly = useUserIsReadOnly()
  const { data: rideResponse, isFetching } = useRideByIdQuery()

  const openCancelRideDialog = useCancelRideDialog()

  const handelOpenCancelDialog = () => {
    openCancelRideDialog({
      ridesId: selectedRideId
    })
  }

  const handleCopyTrackingUrl = () => {
    navigator.clipboard.writeText(rideResponse?.ride?.tracking_url ?? '')
    enqueueSnackbar('Copied to clipboard', { variant: 'success' })
  }

  const ride = rideResponse?.ride

  return (
    <div className="ride-info">
      <div className="ride-info-container">
        {isFetching ? <LinearProgress /> : null}
        {ride ? (
          <>
            <div className={`section section__status ${ride.status}`}>
              <Typography
                fontWeight={700}
                fontSize={17}
                variant="body1">
                {`# ${ride.mta_ref_id}`}
              </Typography>
              <Typography
                className="status-label"
                fontWeight={700}
                fontSize={15}
                textTransform="uppercase"
                letterSpacing={0.33}
                variant="body1">
                {ride.status?.replaceAll('_', ' ')}
              </Typography>
            </div>
            <div className="section section__rider">
              <Avatar>
                {ride.first_name[0]}
                {ride.last_name[0]}
              </Avatar>
              <Typography
                fontWeight={700}
                fontSize={17}
                textTransform="uppercase"
                variant="body1">
                {`${ride.first_name} ${ride.last_name} (${ride.mta_client_id})`}
              </Typography>
              <div className="phone-number">
                <PhoneIcon fontSize="small"/>
                <Typography
                  fontWeight={400}
                  fontSize={15}
                  variant="body1">
                  {formatPhoneNumber(ride.phone_number)}
                </Typography>
              </div>
              <Stack
                justifyContent="center"
                direction="row"
                spacing={1}
                useFlexGap
                flexWrap="wrap">
                {ride.notes.map(note => <Chip key={note} color="primary" label={note} />)}
                {ridesTypesWavIds.includes(ride.ride_type_id) ? <Chip color="primary" label={<AccessibleIcon />} /> : null}
              </Stack>
            </div>
            {(ride.driver_first_name || ride.driver_last_name || ride.driver_phone_number) ? (
              <div className="section section__driver">
                <Avatar>
                  <PersonIcon />
                </Avatar>
                <div>
                  <Typography
                    fontWeight={700}
                    fontSize={17}
                    textTransform="uppercase"
                    variant="body1">
                    {`${ride.driver_first_name ?? ''} ${ride.driver_last_name ?? ''}`}
                  </Typography>
                  <Typography
                    fontWeight={400}
                    fontSize={12}
                    variant="body1">
                    {formatPhoneNumber(ride.driver_phone_number)}
                  </Typography>
                </div>
              </div>
            ) : null }
            {(ride.car_title || ride.car_plate_number) ? (
              <div className="section section__car">
                <DirectionsCarIcon />
                <div>
                  <Typography
                    fontWeight={700}
                    fontSize={17}
                    textTransform="uppercase"
                    variant="body1">
                    {ride.car_plate_number}
                  </Typography>
                  <Typography
                    fontWeight={400}
                    fontSize={12}
                    variant="body1">
                    {ride.car_title}
                  </Typography>
                </div>
              </div>
            ) : null}
            <div className="section section__location">
              <div className="location">
                <div className="pickup">
                  <Typography
                    fontWeight={700}
                    fontSize={16}
                    variant="body1">
                    {`Pick up at • ${moment(ride.pickup_at).format('L LT')}`}
                  </Typography>
                  <Typography
                    color="secondary.contrastText"
                    fontWeight={400}
                    fontSize={16}
                    textTransform="uppercase"
                    variant="body1">
                    {ride.start_address}
                  </Typography>
                </div>
                <div className="dropoff">
                  <Typography
                    fontWeight={700}
                    fontSize={16}
                    variant="body1">
                    {`Drop off at • ${moment(ride.dropoff_at).format('L LT')}`}
                  </Typography>
                  <Typography
                    color="secondary.contrastText"
                    fontWeight={400}
                    fontSize={16}
                    textTransform="uppercase"
                    variant="body1">
                    {ride.destination_address}
                  </Typography>
                </div>
                <Typography
                  color="secondary.contrastText"
                  fontWeight={400}
                  fontSize={16}
                  variant="body1">
                  {`${moment.duration(ride.duration, 'seconds').asMinutes().toFixed()} Min • ${ride.distance.toFixed(2)} Mile`}
                </Typography>
              </div>
              <div>
                <Tooltip title={ride.tracking_url ? 'Copy Share Live Location link' : 'Share Live Location link doesn’t available for this status.'}>
                  <span>
                    <IconButton
                      onClick={handleCopyTrackingUrl}
                      disabled={!ride.tracking_url}
                      className="live-location-button">
                      <LinkIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </div>
            </div>
            <div className="section section__note">
              <TextField
                focused={false}
                fullWidth
                size="small"
                label="Pickup Note"
                value={ride.pickup_note}
                type="text"
                variant="outlined"
                multiline
                rows={4}
                InputProps={{
                  readOnly: true
                }}
                InputLabelProps={{
                  shrink: true
                }} />
            </div>
            <div className="section section__info">
              <Stack
                justifyContent="space-between"
                direction="row"
                spacing={1}
                useFlexGap
                flexWrap="wrap">
                <LabelChip label="Fare Type" value={ride.fare_type?.replaceAll('_', ' ')} />
                <LabelChip label="Ride Type" value={ride.ride_type_display_name} />
                <div className="label-chip">
                  <Typography
                    fontWeight={700}
                    fontSize={16}
                    variant="body1">
                    Equipment
                  </Typography>
                  <Stack direction="row" useFlexGap flexWrap="wrap" spacing={1}>
                    {equipment_codes?.filter(item => ride.equipment_codes.includes(item.value)).map(item => <Chip key={item.value} label={item.label} color="primary" variant="outlined" />)}
                  </Stack>
                </div>
              </Stack>
            </div>
            <div className="section section__created-by">
              <Typography
                fontWeight={700}
                fontSize={16}
                variant="body1">
                {`Created by • ${ride.create_by}`}
              </Typography>
              <Typography
                fontWeight={400}
                fontSize={16}
                variant="body1">
                {`Created at • ${moment(ride.create_at).format('L LT')}`}
              </Typography>
            </div>
            <div className="section section__action">
              {(ride.canceled_at || ride.canceled_by) ? (
                <>
                  <Typography
                    fontWeight={700}
                    fontSize={16}
                    variant="body1">
                    {`Canceled by • ${ride.canceled_by}`}
                  </Typography>
                  <Typography
                    fontWeight={400}
                    fontSize={16}
                    variant="body1">
                    {`Canceled at • ${moment(ride.canceled_at).format('L LT')}`}
                  </Typography>
                </>
              ) : ['user_canceled', 'user_requested_cancel', 'company_canceled', 'driver_canceled', 'finished', 'no_show'].includes(ride.status) ? null : (
                <Tooltip title={isRideOnly ? 'Access Denied' : ''}>
                  <span>
                    <LoadingButton
                      disabled={isRideOnly}
                      onClick={handelOpenCancelDialog}
                      fullWidth
                      size="large"
                      variant="contained"
                      color="primary">
                  Cancel Ride
                    </LoadingButton>
                  </span>
                </Tooltip>
              )}
            </div>
          </>
        ) : null}
      </div>
    </div>
  )
}

export default RideInfo

